import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { firestore, database } from "../../../firebaseConfig";
import { collection, getDocs, doc, updateDoc , getDoc} from "firebase/firestore";
import { ref, set } from "firebase/database";

/**
 * AttachCompanyModal
 * Props:
 * - open, onClose
 * - user: firebase user object (auth.currentUser)
 * - onRequestSent: callback when request created
 *
 * Behavior:
 * - Loads companies from Firestore collection "companies"
 * - Allows farmer to pick one and send an attach request
 * - Writes to Realtime DB: FarmerCompanyRequests/{uid} = {companyName, uid, fullName, email, timestamp, status: 'pending'}
 * - Also sets users/{uid}.pendingAttach in Firestore for quick UI/read by farmer
 * - Enforces only one active request: modal disables send if user.pendingAttach exists
 */

const AttachCompanyModal = ({ open, onClose, user, onRequestSent }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sending, setSending] = useState(false);
  const [farmerDescription, setFarmerDescription] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset description when modal closes
      setFarmerDescription("");
      return;
    }
    let mounted = true;
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const col = collection(firestore, "companies");
        const snap = await getDocs(col);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setCompanies(list);
      } catch (err) {
        console.error("fetchCompanies error", err);
        toast.error("Failed to load companies");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchCompanies();
    return () => {
      mounted = false;
    };
  }, [open]);

  if (!open) return null;

  const sendRequest = async () => {
    if (!user) {
      toast.error("Not authenticated");
      return;
    }
    if (!selectedCompany) {
      toast.error("Select a company first");
      return;
    }
    if (!farmerDescription.trim()) {
      toast.error("Please provide a brief description about yourself");
      return;
    }

    setSending(true);
    try {
      const uid = user.uid;
      const companyName = selectedCompany.companyName || selectedCompany.id;
      const timestamp = new Date().toISOString();

      // Read fullName from Firestore

      const snap = await getDoc(doc(firestore, "users", user.uid));

      let fullName = "";
      if (snap.exists()) {
        fullName = snap.data().fullName || "";
      }


      // Write the request to realtime DB for admins to watch
      const reqRef = ref(database, `Companies/${companyName}/FarmerAttachRequests/${uid}`);
      await set(reqRef, {
        uid,
        companyName,
        fullName,
        email: user.email || "",
        description: farmerDescription.trim(),
        requestedAt: timestamp,
        status: "pending",
      });

      // Also set pendingAttach flag on Firestore users doc for quick UI reads
      const userRef = doc(firestore, "users", uid);
      await updateDoc(userRef, {
        pendingAttach: {
          companyName,
          requestedAt: timestamp,
          status: "pending",
        },
      });

      toast.success(`Request sent to ${companyName}`);
      if (onRequestSent) onRequestSent();
    } catch (err) {
      console.error("sendRequest error", err);
      toast.error("Failed to send request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg w-[760px] max-w-[95%]" onClick={(e)=>e.stopPropagation()}>
        <div className="p-5 border-b">
          <h3 className="text-lg font-semibold text-cyan-700">Link with Company</h3>
          <p className="text-sm text-gray-500">Select a company to request attachment. Only one active request allowed.</p>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-6 text-gray-500">Loading companies…</div>
          ) : companies.length === 0 ? (
            <div className="text-center py-6 text-gray-500">No companies found.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 max-h-[40vh] overflow-y-auto mb-4">
                {companies.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCompany(c)}
                    className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition ${
                      selectedCompany?.id === c.id ? "border-cyan-600 bg-cyan-50" : "border-gray-100"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-cyan-700">{c.companyName || c.id}</h4>
                        <p className="text-xs text-gray-500">{c.registrationNumber || ""}</p>
                      </div>
                      <div className="text-xs text-gray-400">{c.createdAt ? new Date(c.createdAt).getFullYear() : ""}</div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{c.description || ""}</p>
                  </div>
                ))}
              </div>

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About You <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={farmerDescription}
                  onChange={(e) => setFarmerDescription(e.target.value)}
                  placeholder="Briefly describe who you are and where you come from. This helps the company identify and attach you correctly."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  maxLength={300}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {farmerDescription.length}/300 characters
                </p>
              </div>
            </>
          )}
        </div>

        <div className="p-4 flex justify-end gap-3 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded border">Close</button>
          <button
            onClick={sendRequest}
            disabled={sending}
            className="px-4 py-2 rounded bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send Attach Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachCompanyModal;
